import { ReactNode, RefObject, useEffect, useState } from "react";
import { IdType } from "../../types/apiTypes";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { setRemovedIds } from "../../features/companySlice";

type Props<T extends IdType> = {
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
  scrollOffset?: number;
  showScrollTopOffset?: number;
  children: (params: { showScrollTop: boolean; items: T[] }) => ReactNode;
  page: number;
  items: T[];
  containerRef?: RefObject<HTMLElement>;
  setAllItems?: React.Dispatch<React.SetStateAction<T[]>>;
  startPage?: number;
};

export const InfiniteScroll = <T extends IdType>({
  hasMore,
  isLoading,
  loadMore,
  scrollOffset = 2000,
  showScrollTopOffset = 900,
  children,
  page,
  items,
  containerRef,
  setAllItems,
  startPage = 1,
}: Props<T>) => {
  const dispatch = useAppDispatch();

  const [isBottomReached, setIsBottomReached] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [localItems, setItems] = useState<T[]>([]);
  const removedIDs = useAppSelector((state) => state.company.removedIDs);

  useEffect(() => {
    if (setAllItems) {
      setAllItems((prev: T[]) => {
        const isSame =
          prev.length === localItems.length &&
          prev.every((item, i) => item.id === localItems[i]?.id);

        return isSame ? prev : localItems;
      });
    }
  }, [localItems]);

  useEffect(() => {
    const removedID = removedIDs[0];
    if (removedID) {
      setItems((prev) => prev.filter(({ id }) => id !== removedID));
      dispatch(setRemovedIds([]));
    }
  }, [removedIDs]);

  useEffect(() => {
    if (items) {
      if (page === startPage) {
        setItems(items);
      } else {
        setItems((prev) => [...prev, ...items]);
      }
    }
  }, [items]);

  useEffect(() => {
    const container = containerRef?.current ?? window;

    const handleScroll = () => {
      if (container instanceof Window) {
        const { top, height } = document.body.getBoundingClientRect();
        setShowScrollTop(top <= -showScrollTopOffset);
        setIsBottomReached(top <= -height + scrollOffset);
      } else {
        const { scrollTop, scrollHeight, clientHeight } = container;
        setShowScrollTop(scrollTop >= showScrollTopOffset);
        setIsBottomReached(
          scrollTop + clientHeight >= scrollHeight - scrollOffset,
        );
      }
    };
    handleScroll();

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [containerRef, scrollOffset, showScrollTopOffset]);

  useEffect(() => {
    if (isBottomReached && hasMore && !isLoading) {
      loadMore();
      setIsBottomReached(false);
    }
  }, [isBottomReached, hasMore, isLoading, loadMore]);

  return <>{children({ showScrollTop, items: localItems })}</>;
};
